/* eslint-disable */

/*
===============================================================================
Swift Checkout - Shopify Integration
Version: 1.0.9
-------------------------------------------------------------------------------
This script integrates Swift Checkout v2 into Shopify themes.
Version 1.0.9 improvements:
- Debounced observers for performance
- SCV2 logging helper (toggle via window.SCV2_DEBUG)
- Merged/cleaner listeners and minor hygiene improvements
- Fallback stub for handleCheckout if missing (redirect to /checkout)
-------------------------------------------------------------------------------
This script handles:
- Checkout overrides (cart, drawer, PDP, quick-add)
- Customer account order tracking & returns (RMA)
- Swift backend communication (GraphQL)
- Affiliate support
- Optional "Subscribe" button for subscription-tagged products
===============================================================================
*/

/* ============================
   DEBUG & GLOBAL HELPERS

   Toggle debug logs in the console:
   window.SCV2_DEBUG = false; // to silence logs
   window.SCV2_DEBUG = true; // to enable logs
   ============================ */


window.SCV2_DEBUG = window.SCV2_DEBUG === undefined ? true : window.SCV2_DEBUG;

window.SCV2 = {
    log: function () { if (window.SCV2_DEBUG) console.log.apply(console, ['[SCV2]'].concat(Array.from(arguments))); },
    warn: function () { if (window.SCV2_DEBUG) console.warn.apply(console, ['[SCV2]'].concat(Array.from(arguments))); },
    error: function () { console.error.apply(console, ['[SCV2]'].concat(Array.from(arguments))); },
    debug: function () { if (window.SCV2_DEBUG) console.debug.apply(console, ['[SCV2]'].concat(Array.from(arguments))); }
};

function debounce(fn, delay = 120) {
    let timer;
    return function () {
        const args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function () {
            fn.apply(null, args);
        }, delay);
    };
}

// Fallback stub for handleCheckout (Option 3)
// If core implementation isn't available yet, define a temporary stub that redirects
if (typeof window.handleCheckout !== 'function') {
    window.handleCheckout = async function (element, data = {}) {
        SCV2.warn('handleCheckout not defined yet — using fallback redirect to /checkout');
        // Minimal UI feedback if element provided
        try {
            if (element && element.textContent) {
                const prev = element.textContent;
                element.textContent = 'Redirecting...';
                element.disabled = true;
                element.style.opacity = '0.6';
                await new Promise((r) => setTimeout(r, 250));
                // Redirect to Shopify checkout as fallback
                window.location.href = '/checkout';
                // restore (unlikely to reach)
                element.textContent = prev;
                element.disabled = false;
                element.style.opacity = '1';
            } else {
                window.location.href = '/checkout';
            }
        } catch (e) {
            SCV2.error('Fallback handleCheckout failed', e);
            window.location.href = '/checkout';
        }
    };
}

/* ============================
   CORE CONFIG
   ============================ */

const {
    customerId: custId,
    customerEmail,
    customerPhone,
    customerOrder: custOrder,
    customerOrders: custOrders,
    currency,
    brandId,
    scv2Url,
    scv2BOUrl,
    pK,
    scv2Key,
    pageType,
    rmaIsEnabled,
    isLoginRequired,
    loginUrl,
    totalItems,
    validateStock,
    variantInventoryPolicyMap
} = window.scvData || {};

/* ============================================================================
   MAIN ENTRY POINT
   ============================================================================ */
document.addEventListener('DOMContentLoaded', function () {

    /* Load CryptoJS for AES encryption/decryption */
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/crypto-js/4.1.1/crypto-js.min.js';
    document.body.appendChild(script);

    /* ------------------------------------------------------------------------
       Parse URL Parameters
       ------------------------------------------------------------------------ */
    let urlParams = {};
    (window.onpopstate = function () {
        let match,
            pl = /\+/g,
            search = /([^&=]+)=?([^&]*)/g,
            decode = function (s) {
                return decodeURIComponent(s.replace(pl, ' '));
            },
            query = window.location.search.substring(1);

        while ((match = search.exec(query))) {
            if (decode(match[1]) in urlParams) {
                if (!Array.isArray(urlParams[decode(match[1])])) {
                    urlParams[decode(match[1])] = [urlParams[decode(match[1])]];
                }
                urlParams[decode(match[1])].push(decode(match[2]));
            } else {
                urlParams[decode(match[1])] = decode(match[2]);
            }
        }
    })();

    /* ------------------------------------------------------------------------
       Handle success redirect (clear cart after payment success)
       ------------------------------------------------------------------------ */
    if (urlParams && urlParams.success === '1') {
        const loaderContainer = document.createElement('div');
        loaderContainer.style.cssText = `
            position: fixed;
            display: block;
            z-index: 100;
            width: 100%;
            height: 100vh;
            background-color: rgba(255, 255, 255, 0.5);
        `;
        document.body.appendChild(loaderContainer);

        fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        }).then(() => window.location.replace('/'));
    }

    /* ------------------------------------------------------------------------
       Remove all modal-openers that target QuickAdd
       ------------------------------------------------------------------------ */
    document.querySelectorAll('modal-opener[data-modal^="#QuickAdd"]').forEach((opener) => {
        opener.removeAttribute('data-modal'); // disables Shopify’s JS trigger
    });

    document.addEventListener('click', (event) => {
        const btn = event.target.closest('.quick-add__submit[data-product-url]');
        if (!btn) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.href = btn.dataset.productUrl;
    });

    /* Initialize Checkout button overrides */
    createCheckoutButton();
}, { once: true });

/* ============================================================================
   SHOPIFY CART HELPERS
   ============================================================================ */

/**
 * Fetch current Shopify cart data.
 */
async function fetchCartData() {
    const fetchData = await fetch('/cart.js');
    const response = await fetchData.json();
    return response;
}

/**
 * Update Shopify cart items.
 */
async function updateCartData(params) {
    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(params),
    };
    const fetchData = await fetch('/cart/change.js', options);
    const response = await fetchData.json();
    return response;
}

/* ============================================================================
   AUTHENTICATION / CONFIGURATION (GraphQL)
   ============================================================================ */

/**
 * Authenticate with Swift Checkout v2 backend (BO API).
 */
async function loginScv2() {
    const query = `mutation login($key: String!) {
        loginByKey(key: $key) { token }
    }`;

    const params = {
        query,
        variables: { key: scv2Key },
    };

    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify(params),
    };

    const fetchData = await fetch(scv2BOUrl, options);
    const responseData = await fetchData.json();
    return responseData;
}

/**
 * Fetch brand configuration (OMS, channel code, etc.)
 */
async function getConfig() {
    const {
        data: { loginByKey: loginRes },
    } = await loginScv2();

    const query = `query getConfiguration($brandId: String!) {
        getConfiguration(brandId: $brandId) {
            omsUrl
            omsChannelCode
        }
    }`;

    const params = {
        query,
        variables: { brandId },
    };

    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: loginRes.token,
        },
        body: JSON.stringify(params),
    };

    const fetchData = await fetch(scv2BOUrl, options);
    const responseData = await fetchData.json();
    return responseData;
}

/* ============================================================================
   CREATE SWIFT CART
   ============================================================================ */

/**
 * Decide the value of validate_stock for Swift Checkout.
 *
 * Rule:
 * - If any item in the cart is backorder → validate_stock = false
 * - If no items are backorder → validate_stock = true
 *
 * Backorder means:
 * - Shopify allows selling when out of stock (inventory_policy = "continue")
 * - and the stock quantity is 0 or less
 */
function determineValidateStock(cartData) {
    if (!cartData || !cartData.items) {
        console.log('Cart data missing — default validate_stock = true');
        return true;
    }

    const policyMap = variantInventoryPolicyMap || {};

    console.log('Checking cart items for backorder...', policyMap);

    const hasBackorder = cartData.items.some(item => {

        const policy = policyMap[item.variant_id];

        const isBackorder = policy === "continue";

        console.log('Cart item check:', {
            title: item.title,
            variant_id: item.variant_id,
            policy,
            isBackorder
        });

        return isBackorder;
    });

    const validateStock = !hasBackorder;

    console.log('validate_stock computed:', validateStock);

    return validateStock;
}

/**
 * Create cart in Swift Checkout backend.
 */
async function createCart(data) {
    const query = `mutation createCart($input: CreateCartInput) {
        createCart(input: $input) { id }
    }`;

    const items = data.items.map((item) => ({
        qty: item.quantity,
        id: CryptoJS.enc.Base64.stringify(
            CryptoJS.enc.Utf8.parse(`gid://shopify/ProductVariant/${item.variant_id}`)
        ),
    }));

    const inputObj = {};
    if (data?.items_subtotal_price) {
        const subtotal = data.items_subtotal_price.toString().slice(0, -2);
        inputObj.subtotal = parseInt(subtotal);
    }

    const hasAffiliateCode = sessionStorage.getItem('has_affiliate_code');
    if (hasAffiliateCode) {
        const affiliateCode = sessionStorage.getItem('affiliate_code');
        inputObj.additional_info = [{ key: "affiliate_code", value: affiliateCode }];
    }

    const validateStock = determineValidateStock(data);

    const params = {
        query,
        variables: {
            input: {
                customer_id: custId,
                brand_id: brandId,
                validate_stock: validateStock,
                ...inputObj,
                items,
                token: CryptoJS.enc.Base64.stringify(
                    CryptoJS.enc.Utf8.parse(`gid://shopify/Cart/${data.token}`)
                ),
            },
        },
    };

    const options = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(params),
    };

    const fetchData = await fetch(`${scv2Url}/graphql`, options);
    const responseData = await fetchData.json();
    return responseData;
}

/* ============================================================================
   SWIFT CHECKOUT FLOW
   ============================================================================ */

/**
 * Initialize Swift Checkout.
 */
async function swiftCheckout(data = {}, isSubscribe = false) {
    if (isLoginRequired && !custId) {
        window.location.href = loginUrl;
        return;
    }

    const cartData = (Object.keys(data).length > 1 && data.token)
        ? data
        : await fetchCartData();

    SCV2.debug({ data, cartData });

    const checkoutData = await createCart(cartData);

    const gqlErrors = checkoutData?.errors ?? checkoutData?.data?.errors ?? [];
    
    if (gqlErrors.length) {
        const err = gqlErrors[0];
        const reasonRaw = err?.extensions?.reason;

        let reason = null;

        try {
            reason = reasonRaw ? JSON.parse(reasonRaw) : null;
        } catch {
            reason = null;
        }

        const isOOS =
            Array.isArray(reason) &&
            reason.length > 0 &&
            reason.every(r => r && r.variant_id);

        if (isOOS) {
            await applyOutOfStockModal(reason);
        } else {
            showGeneralCheckoutError();
        }

        return;
    }

    sessionStorage.setItem('has_affiliate_code', false);
    sessionStorage.setItem('affiliate_code', '');

    const payload = {
        ecp_token: btoa(custId || ''),
        brand_id: brandId,
        cart_id: checkoutData.data.createCart.id,
        currency,
        email: customerEmail,
        phonenumber: customerPhone,
        isLogin: !!custId,
        totalItems,
        isSubscribe,
    };

    try {
        const key = CryptoJS.enc.Utf8.parse(pK);
        const iv = CryptoJS.enc.Utf8.parse(pK.substr(0, 16));
        let encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), key, { iv, mode: CryptoJS.mode.CBC });
        encrypted = encodeURIComponent(encrypted.toString(CryptoJS.enc.base64));

        window.open(`${scv2Url}/authentication?state=${encrypted}`, '_self');
    } catch (e) {
        SCV2.error('Encryption error:', e);
    }
}

/**
 * Out of Stock Modal.
 */
async function applyOutOfStockModal(items) {

    // Extract numeric variant ID from Shopify GID
    function getVariantNumber(gid) {
        return gid.match(/\d+$/)[0];
    }

    // Shopify cart removal
    async function removeItem(variantId, qty) {
        try {
            await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    updates: {
                        [variantId]: qty
                    }
                })
            });
        } catch (err) {
            console.error('Error removing item from cart:', err);
        }
    }

    // Track what type of change happened
    let reducedItems = [];

    // Remove ALL unavailable items before showing modal
    for (const item of items) {
        if (item.variant_id) {
            const numericId = getVariantNumber(item.variant_id);
            const available = Number(item.available_qty ?? 0);
            const requested = Number(item.requested_qty ?? 0);

            reducedItems.push({
                ...item,
                newQty: available,
                oldQty: requested
            });

            if (available <= 0) {
                await removeItem(numericId, 0);
            } else {
                await removeItem(numericId, available);
            }
        }
    }

    // ========= 1. Inject CSS (only once) =========
    if (!document.getElementById('swift-checkout-oos-style')) {
        const style = document.createElement('style');
        style.id = 'swift-checkout-oos-style';
        style.textContent = `
            .swift-checkout-modal-overlay {
                position: fixed;
                inset: 0;
                background: rgba(0,0,0,0.4);
                z-index: 9999;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .swift-checkout-modal-box {
                background: white;
                padding: 30px;
                border-radius: 12px;
                max-width: 650px;
                box-shadow: 0 10px 25px rgba(0,0,0,0.2);
                text-align: center;
            }

            #swift-checkout-oos-items {
                margin-top: 30px;
                text-align: left;
            }

            .swift-checkout-out-of-stock-item {
                display: flex;
                align-items: center;
                margin-bottom: 12px;
                padding: 6px 0;
            }

            .swift-checkout-out-of-stock-item img {
                width: 60px;
                height: 60px;
                object-fit: cover;
                border-radius: 6px;
                margin-right: 12px;
            }

            .swift-checkout-return-btn {
                margin-top: 20px;
                padding: 12px 25px;
                background: #111;
                color: #fff;
                border-radius: 8px;
                cursor: pointer;
                border: none;
            }

            .swift-oos-info strong {
                display: block;
                font-size: 14px;
            }

            .swift-oos-info small {
                color: #888;
                font-size: 12px;
            }
        `;
        document.head.appendChild(style);
    }


    // ========= 2. Create modal (only once) =========
    let modal = document.getElementById('swift-checkout-oos-modal');

    if (!modal) {
        modal = document.createElement('div');
        modal.id = "swift-checkout-oos-modal";
        modal.className = "swift-checkout-modal-overlay";
        modal.style.display = "none";

        modal.innerHTML = `
            <div class="swift-checkout-modal-box">
                <h2>Out of stock</h2>
                <p>These items are no longer available and have been removed from your cart.</p>
                <div id="swift-checkout-oos-items"></div>
                <button id="swift-checkout-oos-close" class="swift-checkout-return-btn">Return to cart</button>
            </div>
        `;

        document.body.appendChild(modal);

        document
            .getElementById('swift-checkout-oos-close')
            .addEventListener('click', () => {
              window.location.href = '/cart';
            });
    }


    // ========= 3. Fill items =========
    const container = document.getElementById('swift-checkout-oos-items');
    container.innerHTML = '';

    if (reducedItems.length > 0) {
        reducedItems.forEach(item => {
            const el = document.createElement('div');
            el.className = 'swift-checkout-out-of-stock-item';
            el.innerHTML = `
                <img src="${item.image}" alt="${item.title}" />
                <div class="swift-oos-info">
                    <strong>${item.title}</strong>
                </div>
            `;
            container.appendChild(el);
        });
    }

    // ========= 4. Show modal =========
    modal.style.display = 'flex';
}

/**
 * General Error Modal.
 */
function showGeneralCheckoutError() {
    // ================================
    // 1. Inject shared modal CSS (once)
    // ================================
    if (!document.getElementById('swift-checkout-shared-modal-style')) {
        const style = document.createElement('style');
        style.id = 'swift-checkout-shared-modal-style';
        style.textContent = `
            .swift-checkout-modal-overlay {
                position: fixed;
                inset: 0;
                background: rgba(0,0,0,0.4);
                z-index: 9999;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 20px;
            }

            .swift-checkout-error-box {
                background: #fff;
                padding: 30px;
                border-radius: 12px;
                max-width: 520px;
                width: 100%;
                box-shadow: 0 10px 25px rgba(0,0,0,0.2);
                text-align: center;
                animation: swiftModalFade .18s ease-out;
            }

            @keyframes swiftModalFade {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
            }

            .swift-checkout-return-btn {
                margin-top: 20px;
                padding: 12px 25px;
                background: #111;
                color: #fff;
                border-radius: 8px;
                cursor: pointer;
                border: none;
            }

            .swift-checkout-return-btn:hover {
                opacity: 0.9;
            }
        `;
        document.head.appendChild(style);
    }

    // ==================================
    // 2. Ensure modal exists (Shopify-safe)
    // ==================================
    let modal = document.getElementById('swift-checkout-error-modal');

    if (!modal) {
        modal = document.createElement('div');
        modal.id = 'swift-checkout-error-modal';
        modal.className = 'swift-checkout-modal-overlay';
        modal.style.display = 'none';

        modal.innerHTML = `
            <div class="swift-checkout-error-box" role="dialog" aria-modal="true">
                <h2>Something went wrong</h2>
                <p id="swift-checkout-error-message"></p>
                <button id="swift-checkout-error-close" class="swift-checkout-return-btn">
                    Return to cart
                </button>
            </div>
        `;

        // Append to body (safer for Shopify dynamic sections)
        document.body.appendChild(modal);

        // Close handlers
        modal
            .querySelector('#swift-checkout-error-close')
            .addEventListener('click', () => {
                modal.style.display = 'none';
            });

        // click outside to close
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                modal.style.display = 'none';
            }
        });

        // ESC key support
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.style.display === 'flex') {
                modal.style.display = 'none';
            }
        });
    }

    // ================================
    // 3. Set message safely
    // ================================
    const msgEl = document.getElementById('swift-checkout-error-message');
    if (msgEl) {
        msgEl.textContent = 'Please try again later.';
    }

    // ================================
    // 4. Show modal
    // ================================
    modal.style.display = 'flex';
}

/* ============================================================================
   PAYMENT RETRY + TRACK ORDER
   ============================================================================ */

/**
 * Retry failed payments.
 */
async function paymentRetry() {
    const query = `query paymentRetry($brand_id: String!, $order_id: String!) {
        paymentRetry(brand_id: $brand_id, order_id: $order_id) {
            paymentLink
            paymentToken
            vaNumber
        }
    }`;

    const params = {
        query,
        variables: { brand_id: brandId, order_id: custOrder.id },
    };

    const options = {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(params),
    };

    const fetchData = await fetch(`${scv2Url}/graphql`, options);
    const responseData = await fetchData.json();
    return responseData;
}

/**
 * Generate order tracking link.
 */
function getTrackOrderUrl(orderId) {
    const trackOrderState = btoa(orderId + '|' + brandId);
    return `${scv2Url}/track-order/${trackOrderState}`;
}

/* ============================================================================
   OVERRIDE: CUSTOMER ACCOUNT PAGE
   ============================================================================ */

{
    // async function overrideTable() {
    //     let returnUrl = '';
    //     const tbodyChildNodes = document.querySelector('tbody').childNodes;

    //     if (rmaIsEnabled) {
    //         const config = await getConfig();
    //         if (config.data && config.data.getConfiguration && config.data.getConfiguration.omsUrl && config.data.getConfiguration.omsChannelCode) {
    //             const { omsChannelCode, omsUrl } = config.data.getConfiguration;
    //             const encodedQuerystring = `email=${encodeURIComponent(customerEmail)}&channel_code=${encodeURIComponent(
    //                 omsChannelCode
    //             )}&order_number=[orderNumber]&from=${encodeURIComponent(window.location.origin)}`;
    //             returnUrl = `${omsUrl}omsrma/request/index/?${encodedQuerystring}`;
    //         }
    //     }

    //     tbodyChildNodes.forEach((row) => {
    //         for (let i = 0; i < row.children.length; i++) {
    //             const findOrder = custOrders.find((order) => row.children[i].textContent.indexOf(order.id) !== -1);
    //             const beforeLastColumnIdx = row.children.length - 2;
    //             const beforeLastColumn = row.children[beforeLastColumnIdx];

    //             if (findOrder) {
    //                 const orderId = findOrder.id;
    //                 beforeLastColumn.innerHTML = `
    //                     <div class="scv2-orders-links" style="display: flex; justify-content: space-between;">
    //                         <a href="${getTrackOrderUrl(orderId)}" style="text-decoration: underline; text-underline-offset: 0.3em;">${
    //                     beforeLastColumn.textContent
    //                 }</a>
    //                         ${
    //                             rmaIsEnabled && findOrder.fulfillmentStatus === 'fulfilled'
    //                                 ? `<a href="${returnUrl.replace(
    //                                       '[orderNumber]',
    //                                       encodeURIComponent(orderId)
    //                                   )}" style="text-decoration: underline; text-underline-offset: 0.3em;">Return</a>`
    //                                 : ''
    //                         }
    //                     </div>
    //                 `;

    //                 const linksContainer = document.querySelectorAll('.scv2-orders-links');
    //                 linksContainer.forEach((container) => {
    //                     for (let i = 0; i < container.children.length; i++) {
    //                         container.children[i].addEventListener('mouseenter', () => {
    //                             container.children[i].style.opacity = '0.7';
    //                             container.children[i].style['text-decoration-thickness'] = '0.2rem';
    //                         });
    //                         container.children[i].addEventListener('mouseleave', () => {
    //                             container.children[i].style.opacity = '1';
    //                             container.children[i].style['text-decoration-thickness'] = '0.1rem';
    //                         });
    //                     }
    //                 });
    //             }
    //         }
    //     });
    // }
    if (pageType === 'customers/account') {
        /* Override Order Table */
        const hasFulfilledOrder = custOrders.some((order) => order.fulfillmentStatus === 'fulfilled');
        if (hasFulfilledOrder) {
            overrideTable();
        }

        /* Override Address */
        const addressUrl = document.querySelector('a[href="/account/addresses"]');
        addressUrl.href = `${scv2Url}/dashboard`;
    }
}

/* ============================================================================
   OVERRIDE: ORDER DETAIL PAGE
   ============================================================================ */

{
    if (pageType === 'customers/order') {
        async function iterateChildren(element) {
            if (element.children && element.children.length > 0) {
                for (let i = 0; i < element.children.length; i++) {
                    if (element.children[i].textContent.indexOf(custOrder.id) !== -1) {
                        if (element.children[i].children.length === 0) {
                            const orderIdElement = element.children[i];
                            const clonedOrderIdElement = orderIdElement.cloneNode(true);
                            const containerDiv = document.createElement('div');
                            containerDiv.style.cssText = `
                                display: flex;
                                justify-content: space-between;
                            `;

                            const linksContainer = document.createElement('div');
                            linksContainer.style.cssText = `
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                            `;
                            // linksContainer.innerHTML = `
                            //     <a href="${getTrackOrderUrl(custOrder.id)}" style="text-decoration: underline; text-underline-offset: 0.3em; ${
                            //     custOrder.fulfillmentStatus === 'fulfilled' ? 'margin-right: 15px;' : ''
                            // }">Tracking</a>
                            // `;
                            if (rmaIsEnabled && custOrder.fulfillmentStatus === 'fulfilled') {
                                let returnUrl;
                                const config = await getConfig();
                                if (
                                    config.data &&
                                    config.data.getConfiguration &&
                                    config.data.getConfiguration.omsUrl &&
                                    config.data.getConfiguration.omsChannelCode
                                ) {
                                    const { omsChannelCode, omsUrl } = config.data.getConfiguration;
                                    const encodedQuerystring = `email=${encodeURIComponent(customerEmail)}&channel_code=${encodeURIComponent(
                                        omsChannelCode
                                    )}&order_number=${encodeURIComponent(custOrder.id)}&from=${encodeURIComponent(window.location.origin)}`;
                                    returnUrl = `${omsUrl}omsrma/request/index/?${encodedQuerystring}`;
                                }

                                linksContainer.innerHTML += `
                                    <a href="${returnUrl}" style="text-decoration: underline; text-underline-offset: 0.3em;">Return</a>
                                `;
                            }

                            const custOrdersData = custOrders.find(({ id }) => id === custOrder.id);
                            if (custOrdersData.fulfillmentStatus === 'unfulfilled' && custOrdersData.fulfillmentStatusLabel === 'Pending') {
                                let urlPaymentRetry;
                                const paymentRetryData = await paymentRetry();
                                if (paymentRetryData.data && paymentRetryData.data.paymentRetry) {
                                    const { paymentLink } = paymentRetryData.data.paymentRetry;
                                    urlPaymentRetry = paymentLink;
                                }

                                if (urlPaymentRetry !== undefined) {
                                    linksContainer.innerHTML += `
                                    <a href="${urlPaymentRetry}" style="text-decoration: underline; text-underline-offset: 0.3em; margin-left: 15px;">Pay</a>
                                `;
                                }
                            }

                            for (let i = 0; i < linksContainer.children.length; i++) {
                                linksContainer.children[i].addEventListener('mouseenter', () => {
                                    linksContainer.children[i].style.opacity = '0.7';
                                    linksContainer.children[i].style['text-decoration-thickness'] = '0.2rem';
                                });
                                linksContainer.children[i].addEventListener('mouseleave', () => {
                                    linksContainer.children[i].style.opacity = '1';
                                    linksContainer.children[i].style['text-decoration-thickness'] = '0.1rem';
                                });
                            }

                            containerDiv.appendChild(clonedOrderIdElement);
                            containerDiv.appendChild(linksContainer);
                            orderIdElement.replaceWith(containerDiv);

                            break;
                        }
                    }
                    iterateChildren(element.children[i]);
                }
            }
        }

        const mainElChildNodes = document.querySelector('main').childNodes;
        mainElChildNodes.forEach((child) => {
            iterateChildren(child);
        });
    }
}

/* ============================================================================
   OVERRIDE: CHECKOUT, CART, AND PDP
   ============================================================================ */

{
    /**
     * Creates a hidden error container element that can be appended to show an error.
     * @returns {Element}
     */
    function createErrorContainer() {
        const errorContainer = document.createElement('div');
        errorContainer.classList.add('scv2-errors-container');
        errorContainer.style.cssText = `
            color: red;
            padding: 5px 0;
            display: none;
        `;
        return errorContainer;
    }

    /**
     * Handles UI state changes when the checkout flow is initiated.
     * - shows loading state on the element
     * - calls swiftCheckout and recovers UI on errors
     * @param {HTMLButtonElement} element
     * @param {Object} data
     */
    async function handleCheckout(element, data = {}) {
        let elementText;

        try {
            elementText = element.textContent;
            element.textContent = 'Loading...';
            element.disabled = true;
            element.style.opacity = '0.5';
            if (Object.keys(data).length === 0) {
                await swiftCheckout();
            } else {
                await swiftCheckout(data);
            }
        } catch (e) {
            let errorContainer = document.querySelector('div.scv2-errors-container');
            if (errorContainer) {
                errorContainer.textContent = '';
            } else {
                errorContainer = createErrorContainer();
            }

            element.textContent = elementText;
            element.disabled = false;
            element.style.opacity = '1';

            const parentNode = element.parentNode;
            if (parentNode) {
                parentNode.appendChild(errorContainer);
            }
            errorContainer.textContent = 'Something went wrong. Please try again later.';
            errorContainer.style.display = 'block';
        }
    }

    /**
     * Finds cart checkout button(s) and overrides them to use swiftCheckout.
     * - preserves the existing DOM placement of the original button
     * - changes the button type to 'button' and attaches onclick handler
     */
    function getCheckoutButton() {
        const checkoutButton = document.querySelectorAll('button[name="checkout"]');
        const newButtonContainer = document.createElement('div');
        if (checkoutButton && checkoutButton.length > 0) {
            const lastCheckoutButton = checkoutButton[checkoutButton.length - 1];
            const buttonParentNode = lastCheckoutButton.parentNode;
            newButtonContainer.style.width = '100%';
            newButtonContainer.appendChild(lastCheckoutButton);
            buttonParentNode.appendChild(newButtonContainer);

            // Keep the visible label but ensure it is "Checkout"
            lastCheckoutButton.innerText = buttonParentNode.innerText || 'Check out';

            // Convert to non-submitting button and attach our handler
            lastCheckoutButton.type = 'button';
            lastCheckoutButton.classList.add('scv2-checkout-button');
            lastCheckoutButton.disabled = false;
            lastCheckoutButton.removeAttribute('aria-disabled');
            lastCheckoutButton.style.opacity = '1';
            lastCheckoutButton.onclick = () => handleCheckout(lastCheckoutButton);
        }
    }
    getCheckoutButton();

    /* ----------------------------------------------------------------------
       Re-apply override when cart drawer re-renders (Shopify Refresh theme).
       Observes the <cart-drawer> element for changes and re-applies override.
       ---------------------------------------------------------------------- */
    // Swift Override — Prevent Cart Drawer when already on /cart
    const isCartPage = window.location.pathname === '/cart' || window.location.pathname === '/cart/';

    if (isCartPage) {
        document.addEventListener('DOMContentLoaded', () => {
            const cartIcon = document.getElementById('cart-icon-bubble');

            if (cartIcon) {
                // Clone to remove Shopify's built-in drawer click behavior
                const newCartLink = cartIcon.cloneNode(true);

                // Replace original with clean clone
                cartIcon.parentNode.replaceChild(newCartLink, cartIcon);

                // Force it to simply reload /cart
                newCartLink.removeAttribute('role');
                newCartLink.removeAttribute('aria-haspopup');
                newCartLink.removeAttribute('id');
                newCartLink.setAttribute('href', '/cart');
                newCartLink.style.cursor = 'pointer';

                // Optional — force reload on click (even if Shopify caches it)
                newCartLink.addEventListener('click', (event) => {
                    event.preventDefault();
                    SCV2.log('♻️ Already on cart page — refreshing instead of opening drawer');
                    window.location.reload();
                });

                SCV2.log('🛒 Swift Override Active → Cart icon reloads instead of opening drawer (on /cart page)');
            }
        });
    }

    // Cart Drawer Observer — Keep checkout override logic intact
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer) {
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList' || mutation.type === 'attributes') {
                    const checkoutBtn = cartDrawer.querySelector('button[name="checkout"]');

                    if (checkoutBtn && !checkoutBtn.classList.contains('scv2-checkout-button')) {
                        SCV2.log('🛒 Re-applying SCV2 checkout override (cart drawer refreshed)');
                        getCheckoutButton(); // Re-apply your override logic
                    }
                }
            }
        });

        observer.observe(cartDrawer, {
            childList: true,
            attributes: true,
            subtree: true,
        });
    }

    /* ----------------------------------------------------------------------
       Handle 'cart:refresh' event for themes that re-render cart (e.g., Prestige).
       - waits for checkout button to reappear and re-applies override
       - also sets up a mini-cart mutation observer to reapply when necessary
       ---------------------------------------------------------------------- */
    document.addEventListener('cart:refresh', async () => {
        const checkoutButton = document.querySelector('button[name="checkout"]');
        if (checkoutButton) {
            checkoutButton.setAttribute('data-rerendercart', 'false');
        }

        async function rerenderCart() {
            return new Promise((resolve) => {
                const cartRerenderInterval = setInterval(() => {
                    const rerenderedCheckoutButton = document.querySelector('button[name="checkout"]');
                    if (rerenderedCheckoutButton) {
                        // Temporarily disable while we evaluate
                        rerenderedCheckoutButton.disabled = true;
                        rerenderedCheckoutButton.setAttribute('aria-disabled', true);
                        rerenderedCheckoutButton.style.opacity = '0.5';

                        if (!rerenderedCheckoutButton.hasAttribute('data-rerendercart')) {
                            getCheckoutButton();
                            resolve();
                            clearInterval(cartRerenderInterval);
                        }
                    }
                }, 200);
            });
        }
        await rerenderCart();

        /* Listens to DOM changes when mini-cart updates after changing quantity and adding product to cart. */
        const miniCart = document.getElementById('sidebar-cart');
        if (miniCart) {
            const observer = new MutationObserver(function (mutations) {
                mutations.forEach(async (mutation) => {
                    if (mutation.addedNodes.length > 0) {
                        await rerenderCart();
                    }
                });
            });
            observer.observe(miniCart, { attributes: true, childList: true, characterData: true });
        }
    });

    /* ----------------------------------------------------------------------
       Overrides "Buy it now" button on PDP
       - hides Shopify's dynamic payment button and replaces with a cloned
         button that adds item to cart and redirects to /cart
       ---------------------------------------------------------------------- */
    function createCheckoutButton() {
        if (pageType === 'product') {
            const paymentButton = document.querySelector('div[data-shopify="payment-button"]');
            if (!paymentButton) return;

            const buynowButton = document.querySelector('div[data-shopify="payment-button"] button');
            const formContainer = document.querySelector('form[action*="/cart/add"]');
            if (!buynowButton || !formContainer) return;

            // Hide Shopify's original dynamic button
            paymentButton.style.display = 'none';

            // Clone the button to keep the same style
            const buttonClone = buynowButton.cloneNode(true);
            buttonClone.id = 'scv2-checkout-button';
            buttonClone.textContent = buynowButton.textContent || 'Buy it now';
            buttonClone.type = 'button';
            buttonClone.disabled = false;
            buttonClone.style.opacity = '1';

            // 🛒 When clicked → add item to cart → redirect to /cart
            buttonClone.addEventListener('click', async () => {
                try {
                    const variantId = formContainer.querySelector('input[name="id"]').value;
                    const quantity = Number(document.querySelector('input[name="quantity"]').value || 1);

                    // Add to cart
                    const addToCartRes = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
                    });

                    if (!addToCartRes.ok) throw new Error('Failed to add item to cart');

                    // ✅ Redirect after success
                    window.location.href = '/cart';
                } catch (err) {
                    SCV2.error('Add to cart failed:', err);
                    alert('Failed to add item to cart. Please try again.');
                }
            });

            // Insert cloned button and remove original
            const buttonContainer = document.createElement('div');
            buttonContainer.appendChild(buttonClone);
            paymentButton.parentNode.appendChild(buttonContainer);
            paymentButton.remove();
        }
    }

    /* ============================================================================
        SWIFT CHECKOUT — CART DRAWER INTEGRATION
        Ensures Swift Checkout overrides any custom theme cart drawer checkout button
       ============================================================================ */
    (() => {
        function applyCartDrawerOverride() {
            const drawer = document.querySelector('.cart-drawer__inner');
            if (!drawer) return;

            const checkoutBtn = drawer.querySelector('.cart__checkout-button[name="checkout"]');
            if (!checkoutBtn) return;

            // Avoid re-initializing if already converted
            if (checkoutBtn.classList.contains('scv2-checkout-button')) return;

            SCV2.log('🛍️ SCV2 override → Cart drawer checkout button detected');

            checkoutBtn.type = 'button';
            checkoutBtn.classList.add('scv2-checkout-button');
            checkoutBtn.disabled = false;
            checkoutBtn.removeAttribute('aria-disabled');
            checkoutBtn.style.opacity = '1';

            checkoutBtn.addEventListener('click', async () => {
                try {
                    await handleCheckout(checkoutBtn);
                } catch (e) {
                    SCV2.error('SCV2 Checkout error (cart drawer):', e);
                }
            });
        }

        // Observe drawer changes (re-renders on quantity or open/close)
        const drawerRoot = document.querySelector('cart-drawer, .cart-drawer__inner');
        if (drawerRoot) {
            const observer = new MutationObserver(() => {
                applyCartDrawerOverride();
            });
            observer.observe(drawerRoot, { childList: true, subtree: true });
        }

        // Apply immediately when DOM is ready
        document.addEventListener('DOMContentLoaded', () => {
            applyCartDrawerOverride();
        });

        // Reapply when cart refreshes
        document.addEventListener('cart:refresh', () => {
            applyCartDrawerOverride();
        });
    })();

    /* ============================================================================
        SWIFT CHECKOUT — QUICK ADD MODAL "BELI SEKARANG" REDIRECT TO CART
        Overrides the Quick Add modal button to add item to cart and redirect
       ============================================================================ */
    (() => {
        function applyQuickAddModalRedirect() {
            const modal = document.querySelector('#quick-add-modal-content');
            if (!modal) return;

            const quickAddBtn = modal.querySelector(
                '.shopify-payment-button__button--unbranded, .shopify-payment-button__button'
            );
            if (!quickAddBtn) return;

            // Avoid re-initializing
            if (quickAddBtn.classList.contains('scv2-checkout-button')) return;

            SCV2.log('🧩 SCV2 override → Quick Add modal "Beli sekarang" redirect active');

            // Clone to remove Shopify handlers
            const redirectBtn = quickAddBtn.cloneNode(true);
            redirectBtn.classList.add('scv2-checkout-button');
            redirectBtn.type = 'button';
            redirectBtn.disabled = false;
            redirectBtn.removeAttribute('aria-disabled');
            redirectBtn.style.opacity = '1';
            redirectBtn.textContent = quickAddBtn.textContent || 'Beli sekarang';

            // 🛒 Add to cart then redirect
            redirectBtn.addEventListener('click', async () => {
                try {
                    const form = modal.querySelector('form[action*="/cart/add"]');
                    if (!form) {
                        SCV2.warn('⚠️ No add-to-cart form found inside Quick Add modal');
                        return;
                    }

                    const variantIdInput = form.querySelector('input[name="id"]');
                    const quantityInput = form.querySelector('input[name="quantity"]');
                    const variantId = variantIdInput ? variantIdInput.value : null;
                    const quantity = quantityInput ? Number(quantityInput.value) || 1 : 1;

                    if (!variantId) {
                        alert('Please select a variant before checkout.');
                        return;
                    }

                    // Add to cart
                    const res = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
                    });

                    if (!res.ok) throw new Error('Failed to add item to cart');

                    SCV2.log('✅ Item added — redirecting to cart');
                    window.location.href = '/cart';
                } catch (err) {
                    SCV2.error('❌ Quick Add redirect error:', err);
                    alert('Failed to add to cart. Please try again.');
                }
            });

            // Replace original button
            quickAddBtn.replaceWith(redirectBtn);
        }

        // Observe for modal content updates (Shopify replaces it dynamically)
        const modalEl = document.querySelector('#quick-add-modal-content');
        if (modalEl) {
            const modalObserver = new MutationObserver(() => {
                applyQuickAddModalRedirect();
            });
            modalObserver.observe(modalEl, { childList: true, subtree: true });
        }

        // Apply once on DOM ready
        document.addEventListener('DOMContentLoaded', () => {
            applyQuickAddModalRedirect();
        });
    })();

    /* ------------------- PDP "Subscribe" Button -------------------------- */
    {
        if (pageType === 'product') {
            async function fetchProductData() {
                const productUrl = `${window.Shopify.routes.root}${window.location.pathname.replace('/', '')}.js`;
                const fetchData = await fetch(productUrl);
                const response = await fetchData.json();
                return response;
            }

            async function generateSubscribeButton() {
                const fetchProduct = await fetchProductData();

                if (fetchProduct?.tags.length > 0 && fetchProduct.tags.some((tag) => tag === 'subscription')) {
                    const buyNowButton = document.querySelector('.shopify-payment-button__button');
                    if (!buyNowButton) return;

                    const parentNode = buyNowButton.parentNode;
                    const cloneBuyNowButton = buyNowButton.cloneNode(true);
                    cloneBuyNowButton.textContent = 'Subscribe';
                    cloneBuyNowButton.id = 'scv2-subscribe-button';

                    const formContainer = document.querySelector('form input[name="form_type"][value="product"]').parentNode;

                    parentNode.appendChild(cloneBuyNowButton);
                    cloneBuyNowButton.onclick = () => {
                        const variantSelector = ['input', 'select'];
                        const pdpLineItems = { items: [] };

                        /* Iterate over variants element selector, current known uses: input, select  */
                        variantSelector.forEach((selector) => {
                            if (formContainer.querySelector(`${selector}[name="id"]`)) {
                                pdpLineItems.items.push({
                                    quantity: Number(document.querySelector('input[name="quantity"]').value),
                                    variant_id: formContainer.querySelector(`${selector}[name="id"]`).value,
                                });
                            }
                        });
                        swiftCheckout(pdpLineItems, true);
                    };
                }
            }
            generateSubscribeButton();
        }
    }
}
